import request from "@/providers/requests";
import ENDPOINTS from "@/providers/end-points";

// Interfaces
import { Response } from "@/interface/response.interface";
import { ItemInterface, SummariseItemsInterface } from "@/interface/business/item.interface";
import { OrderOptionsInterface } from "@/interface/business/order.interface";
import { ItemDiscountInterface } from "@/interface/business/discount.interface";
import { DeliveryFeesInterface } from "@/interface/business/delivery.interface";

// Types
import { ItemType, SummaryItemType } from "@/types/business/item.type";
import { DeliveryFeesType } from "@/types/business/delivery.type";
import { PopularListType } from "@/types/locations/popular";
import { OrderOptionType } from "@/types/orders";

//Modules
import { DeliveryFees, Item, ItemDiscount, OrderOption, Popular, SummariseItems } from "@/modules/location";
import Storage from "@/services/storage";
import { StorageConstants } from "@/enums/storage.enum";

/**
 * Location Store Module
 */
export default {
  namespaced: true,

  state: {
    items: [],
    item: null,
    summariseItems: [],
    orderOptions: [],
    itemDiscounts: [],
    deliveryFees: [],
    populars: [],
    locationId: null,
  },

  getters: {
    itemsList: (state: any): Array<ItemInterface> => state.items,
    itemDetails: (state: any): ItemInterface => state.item,
    orderOptions: (state: any): Array<OrderOptionsInterface> => state.orderOptions,
    deliveryFees: (state: any): Array<DeliveryFeesInterface> => state.deliveryFees,
    ItemDiscounts: (state: any): Array<ItemDiscountInterface> => state.itemDiscounts,
    SummariseItemsList: (state: any): SummariseItemsInterface => state.summariseItems,
    Populars: (state: any): Array<PopularListType> => state.populars,
    LocationId: (state: any): any => state.locationId,
  },

  mutations: {
    SET_ITEMS_LIST(state: any, payload) {
      state.items = payload;
    },

    SET_ORDER_OPTIONS_LIST(state: any, payload = []) {
      state.orderOptions = payload;
    },

    SET_ITEM_DISCOUNT_LIST(state: any, payload) {
      state.itemDiscounts = payload;
    },

    SET_SUMMARISE_ITEMS_LIST(state: any, payload = []) {
      state.summariseItems = payload;
    },

    SET_ITEM_DETAILS(state: any, payload = null) {
      state.item = payload;
    },

    SET_DELIVERY_FEES(state: any, payload = []) {
      state.deliveryFees = payload;
    },

    SET_POPULAR(state: any, payload = []) {
      state.populars = payload;
    },

    SET_LOCATION_BY_AREA(state: any, payload) {
      state.locationId = payload;
    },
  },

  actions: {
    async getSummaryItemsCategorise({ commit }: { commit: any }, { location_id, mode, page_size }) {
      return await request
        .get<Response<Array<SummaryItemType>>>(ENDPOINTS.ITEMS(location_id, mode, page_size))
        .then((response) => response.data)
        .then(async ({ data }) => {
          commit("SET_SUMMARISE_ITEMS_LIST", new SummariseItems(data));
        })
        .catch(async (error) => {
          commit("SET_SUMMARISE_ITEMS_LIST", null);
        });
    },

    async getSummaryDigitalMenuItemsCategorise({ commit }: { commit: any }, { location_id, mode, page_size }) {
      return await request
        .get<Response<Array<SummaryItemType>>>(ENDPOINTS.ITEMS(location_id, mode, page_size))
        .then((response) => response.data)
        .then(async ({ data }) => {
          commit("SET_SUMMARISE_ITEMS_LIST", new SummariseItems(data, true));
        })
        .catch(async (error) => {
          commit("SET_SUMMARISE_ITEMS_LIST", null);
        });
    },

    async getItem({ commit }: { commit: any }, payload) {
      return await request
        .get<Response<ItemType>>(ENDPOINTS.ITEM(payload.locationId, payload.itemId))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const item = new Item(data);
          commit("SET_ITEM_DETAILS", item);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getOrderOptions({ commit }: { commit: any }, payload) {
      return await request
        .get<Response<Array<OrderOptionType>>>(ENDPOINTS.LOCATION_ORDER_OPTIONS(payload))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const order_options = data.map((option) => new OrderOption(option));
          commit("SET_ORDER_OPTIONS_LIST", order_options);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getItemDiscounts({ commit }: { commit: any }, payload) {
      const { location_id = "", category_id = "", item_id = "" } = payload;
      return await request
        .get<Response<Array<ItemDiscountInterface>>>(
          ENDPOINTS.LOCATION_ITEM_DISCOUNTS(location_id, category_id, item_id)
        )
        .then((response) => response.data)
        .then(async ({ data }) => {
          const discounts = data.map((item) => new ItemDiscount(item));
          commit("SET_ITEM_DISCOUNT_LIST", discounts);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getDeliveryFees({ commit }: { commit: any }, payload) {
      return await request
        .get<Response<Array<DeliveryFeesType>>>(ENDPOINTS.DELIVERY_FEES(payload.location_id, payload.area_id))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const deliveryFees = data.map((fees) => new DeliveryFees(fees));
          commit("SET_DELIVERY_FEES", deliveryFees);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getBranchMostPopularItems({ commit }: { commit: any }, payload) {
      return await request
        .get<Response<Array<PopularListType>>>(ENDPOINTS.MOST_POPULAR(payload))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const popular = new Popular(data);
          commit("SET_POPULAR", popular);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getLocationByAreaId({ commit, store }: { commit: any; store: any }, { areaId }) {
      return await request
        .get<Response<any>>(`${ENDPOINTS.LOCATION_BY_AREA}?area_list=${areaId}`) //areaIds:string
        .then((response) => response.data)
        .then(async ({ data }) => {
          // if result empty set default location null and return ASAP
          if (window._.isEmpty(data)) {
            return await commit("SET_LOCATION_BY_AREA", "");
          }

          // set selected default location
          const selectedLocation = data.find((location) => location.is_default);
          const locations: Array<any> = [];

          // set available locations
          await Storage.get(StorageConstants.LOCATIONS).forEach((location) => {
            const availableLocation = data.find((item) => item.location_id === location.id);
            if (availableLocation) {
              locations.push(location);
            }
          });

          // set available locations to store
          await commit("Orders/SET_ORDER_LOCATIONS_LIST", locations, { root: true });

          // set default location to store
          await commit("SET_LOCATION_BY_AREA", selectedLocation?.location_id || "");
          return;
        })
        .catch(async (error) => {
          await commit("SET_LOCATION_BY_AREA", "");
        });
    },
  },
};
